import { Money } from './money.js';
/**
 * The ONE currency-conversion convention (FIN-05 BR-FIN-05-01/-02, F-10,
 * EC-19-57 — "the rule is fixed, the tables must be brought into line"):
 *
 *   A rate is how many units of THAT currency equal ONE unit of the base.
 *
 *   base → foreign :  foreign = base × rate      (MULTIPLY)
 *   foreign → base :  base    = foreign ÷ rate   (DIVIDE)
 *
 * PART 18's F-10 verified this against five independent places in the
 * source (the base's implicit rate of 1, Cash Transfer's cross-rate
 * multiply, and the divides in the bank dashboard, the revaluation and the
 * payroll totals), and the shipped pegs settle it — AED 3.6725 and SAR
 * 3.7500 are per-dollar figures; under the opposite reading one dirham
 * would be worth $3.67.
 *
 * Every converting module calls these two functions rather than restating
 * the arithmetic: "a restatement in the opposite direction produces a
 * 40-times error on a landed cost, and because an inverted rule combined
 * with an inverted stored rate still yields the right answer, the mistake
 * survives arithmetic checking" (FIN-05 §5b).
 *
 * Rounding: conversions round only at the end (FIN-05 §15), which these
 * helpers are — one multiply or divide, then round to 2.
 */
/** Base → foreign: MULTIPLY by the rate (BR-FIN-05-02). */
export function convertBaseToForeign(base, rate, foreignCurrency) {
    return Money.of(base.toFixed2(), foreignCurrency).times(rate).rounded();
}
/** Foreign → base: DIVIDE by the rate (BR-FIN-05-02, BR-FIN-05-08). */
export function convertForeignToBase(foreign, rate, baseCurrency) {
    return Money.of(foreign.toFixed2(), baseCurrency).dividedBy(rate).rounded();
}
const RATE_PATTERN = /^\d{1,11}(\.\d{1,4})?$/;
/*
 * The arithmetic below is on RATES, not amounts, and it is exact BigInt
 * arithmetic at scale 4 — the very thing the no-raw-money-arithmetic rule
 * exists to force. Money never touches it: every amount that meets a rate
 * does so through convertBaseToForeign / convertForeignToBase above, which
 * are Money operations. Rates must not pass through JS floats any more than
 * money may, and BigInt is how that is guaranteed here.
 */
/* eslint-disable money/no-raw-money-arithmetic */
/** A rate string (≤4 decimals, > 0) as a BigInt scaled by 10^4. */
function rateScaled(rate) {
    const trimmed = rate.trim();
    if (!RATE_PATTERN.test(trimmed)) {
        throw new Error(`not a rate (up to four decimals, FIN-05 §15): ${rate}`);
    }
    const [whole = '0', frac = ''] = trimmed.split('.');
    const scaled = BigInt(whole) * 10000n + BigInt(frac.padEnd(4, '0') || '0');
    if (scaled <= 0n)
        throw new Error(`a rate of zero or less is refused (FIN-05 §15): ${rate}`);
    return scaled;
}
/**
 * The cross rate between two non-base currencies, the Cash Transfer way
 * (FIN-05 §5b: "computes its cross rate as the destination's rate divided
 * by the source's and then multiplies"). Returned at four decimals — the
 * table's own precision (FIN-05 §15) — rounded half-up, in scaled-integer
 * arithmetic: rates must not pass through JS floats any more than money may.
 */
export function crossRate(fromRate, toRate) {
    const from = rateScaled(fromRate);
    const to = rateScaled(toRate);
    // (to / from) at scale 4, half-up: (to·10^4·2 + from) / (from·2).
    const scaled = (to * 10000n * 2n + from) / (from * 2n);
    const whole = scaled / 10000n;
    const frac = (scaled % 10000n).toString().padStart(4, '0');
    return `${whole.toString()}.${frac}`;
}
/* eslint-enable money/no-raw-money-arithmetic */
//# sourceMappingURL=fx.js.map